
import {
  Component, EventEmitter, Input, Output,
  computed, signal, OnChanges, SimpleChanges, OnInit, ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RmsModule } from '../../rms-module/rms.module';
import { ContactFieldKey, ContactModel } from '@interfaces';

export interface ContactSelectOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'contact-card',
  standalone: true,
  imports: [RmsModule, FormsModule],
  templateUrl: './contact-card.component.html',
  styleUrls: ['./contact-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactCardComponent implements OnInit, OnChanges {
  @Input() title: string = '';
  @Input() contactData: ContactModel[] = [];
  @Input() canEdit: boolean = true;
  @Input() fields: ContactFieldKey[] = [
    'firstName', 'lastName',
    'email', 'phone',
    'street1', 'city', 'state', 'postalCode', 'country',
    'messenger'
  ];
  @Input() model: ContactModel | null = null;

  @Output() save = new EventEmitter<ContactModel>();

  private readonly builtInFields = new Set<ContactFieldKey>([
    'firstName', 'lastName', 'company',
    'email', 'phone',
    'street1', 'street2', 'city', 'state', 'postalCode', 'country',
    'messenger', 'notes', 'fax'
  ]);


  get dynamicFields(): ContactFieldKey[] {
    return (this.fields || []).filter(k => !this.builtInFields.has(k));
  }


  edit = false;
  selectedContactId: string | number | null = null;

  // Local form state (never mutate the returned object; always set/update)
  form = signal<ContactModel>({});

  // Track whether we already hydrated from inputs, so we don't wipe user edits
  private hydratedOnce = false;

  mailingLabel = computed(() => {
    const v = this.form();
    const name = [v.firstName, v.lastName].filter(Boolean).join(' ');
    const lines: string[] = [];
    if (name) lines.push(name);
    if (v.company) lines.push(v.company);
    if (v.street1) lines.push(v.street1);
    if (v.street2) lines.push(v.street2);
    const cityStateZip = [v.city, [v.state, v.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    if (cityStateZip) lines.push(cityStateZip);
    if (v.country) lines.push(v.country);
    return lines.filter(Boolean);
  });

  prettifyLabel(raw: string): string {
    if (!raw) return '';
    // Convert snake_case, kebab-case, camelCase to words
    const spaced = raw
      .replace(/[_\-]+/g, ' ')
      .replace(/([a-z\d])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    // Uppercase first letter of each word
    return spaced.replace(/\b\w/g, m => m.toUpperCase());
  }

  get hasContacts() { return (this.contactData?.length || 0) > 1; }

  // -------------------- Lifecycle --------------------

  ngOnInit() {
    this.initialHydrate();
  }


  ngOnChanges(changes: SimpleChanges) {
    // Do NOT clobber local edits
    if (this.edit) return;

    // If model reference changed, use it
    if (changes['model']) {
      if (this.model) {
        this.applyModel(this.model);
        this.hydratedOnce = true;
        return;
      }
      // model became null: if never hydrated, try from contacts; else keep local form
      if (!this.hydratedOnce) {
        this.hydrateFromContactsOrBlank();
      }
      return;
    }

    // ✅ Rehydrate when contactData changes (even after hydratedOnce)
    if (changes['contactData']) {
      const curr = changes['contactData'].currentValue as ContactModel[] | undefined;
      if (!this.edit) {
        if (curr && curr.length > 0) {
          // Reset selection to the first contact for the new supplier
          this.selectedContactId = curr[0]?.id ?? 0;
          this.applyContact(curr[0]);
        } else {
          this.applyBlank();
          this.selectedContactId = null;
        }
        this.hydratedOnce = true;
      }
    }
  }


  // -------------------- Hydration helpers --------------------

  private initialHydrate() {
    if (this.hydratedOnce) return;

    if (this.model) {
      this.applyModel(this.model);
      this.hydratedOnce = true;
      return;
    }

    this.hydrateFromContactsOrBlank();
    this.hydratedOnce = true;
  }

  private hydrateFromContactsOrBlank() {
    const list = this.contactData || [];
    if (list.length >= 1) {
      const first = list[0];
      this.selectedContactId = first?.id ?? 0;
      this.applyContact(first);
    } else {
      // Only produce a blank once on first hydrate
      this.applyBlank();
    }
  }

  private applyModel(m: ContactModel) {
    // Always set a fresh object ref
    this.form.set({ ...m });
    this.selectedContactId = m.id ?? null;
  }

  private applyContact(c: ContactModel) {
    this.form.set({ ...c });
  }

  private applyBlank() {
    const empty: ContactModel = {};
    this.fields.forEach(k => { (empty as any)[k] = ''; });
    this.form.set(empty);
  }

  // -------------------- UI interactions --------------------

  get contactOptions(): ContactSelectOption[] {
    return (this.contactData || []).map((c, idx) => {
      const label = this.formatContactLabel(c);
      const value = (c.id ?? idx) as string | number;
      return { label, value };
    });
  }

  private formatContactLabel(c: ContactModel): string {
    const name = [c.firstName, c.lastName].filter(Boolean).join(' ');
    if (name) return name;
    if (c.company) return c.company!;
    if (c.email) return c.email!;
    if (c.phone) return c.phone!;
    return `Contact ${c.id ?? ''}`.trim();
  }

  loadFromSelected() {
    const list = this.contactData || [];
    const sel = this.normalizeId(this.selectedContactId);

    // Try id match first
    let chosen = list.find(c => this.normalizeId(c.id) === sel);

    // Fallback to index if selectedContactId is a number
    if (!chosen && typeof this.selectedContactId === 'number') {
      chosen = list[this.selectedContactId];
    }

    if (chosen) {
      this.applyContact(chosen);
      this.hydratedOnce = true; // now we're hydrated from a selection
    }
    // If not found, KEEP current form (do NOT reset)
  }

  newBlank() {
    this.selectedContactId = null;
    this.applyBlank();
    if (this.canEdit) this.edit = true;
    this.hydratedOnce = true;
  }

  toggleEdit() {
    if (!this.canEdit) return;
    this.edit = !this.edit;
  }

  onDone() {
    const payload: ContactModel = { ...this.form() };
    // Preserve id for known selection
    if (payload.id == null && this.selectedContactId != null) {
      const chosen = (this.contactData || []).find(
        c => this.normalizeId(c.id) === this.normalizeId(this.selectedContactId)
      );
      if (chosen?.id != null) payload.id = chosen.id;
    }
    // Debug:
    // console.log('Saving contact payload:', payload);
    this.save.emit(payload);
    this.edit = false;
  }

  // -------------------- State utils --------------------

  private normalizeId(v: string | number | null | undefined): string {
    return v == null ? '' : String(v);
  }

  // IMPORTANT: immutably assign the ONE changed key
  setField<K extends ContactFieldKey>(key: K, value: ContactModel[K]) {
    // Debug:
    // console.log('setField', key, value, 'prev:', this.form());
    this.form.update(f => ({
      ...f,
      [key]: (value ?? '') as any
    }));
    // console.log('next:', this.form());
  }

  trackByKey(_: number, key: string) { return key; }

  showField(key: ContactFieldKey) { return this.fields.includes(key); }
}
